/**
 * 草稿页的「整理一下」（TEACH-UNDERSTAND-UI-117；接口照 adm1 TEACH-WIRE-117 HTTP-CONTRACT 第二节，I 10-17 定说法）：
 * 把描述和参考照片整理成一份教学说明，主人看过、确认了才开始教。
 * - 请求只带页面上那一版的 draft_revision，不带幂等键：同一版同一轮后端只整理一次，再点给原结果（replayed，照同一个结果处理）。
 * - 描述改过还没存时先不整理（整理的是存上的那一版），说清先点「改好了」；没写描述也先不整理。
 * - 六种结果：
 *   ready 已经写成新一版教学说明，单子变成等你确认：直接换成确认页，焦点落在说明表上（确认页没有标题卡）；
 *   need_answer 出最多 2 个问题，每个最多 60 字，回答走第二条路（同样只带 draft_revision）；
 *   split 一次教了几个动作：挑一个先教（把描述换成它，再整理），别的可以另存成草稿（新草稿 refs 只能是空的）；
 *   not_supported 说哪些学不了，给简单一点的教法，可以照它改描述；unusable 请主人改一改描述再整理；
 *   unknown 结果还在核对：照实说，不重试、也不给按钮。
 * - 错误各说各的：同一版正在整理（409）过几秒刷新单子、不另发；整理途中描述改了（409）刷新单子再点；这次没发出去（503）给「再试一次」；
 *   整理没装（503 unavailable）说「现在还整理不了，过一会儿再来」、不给按钮；今天的次数到了（429）说「今天先到这里，明天再整理」、不给按钮。
 *   这里的次数是平台的整理次数，不是主人的赠送学习机会，不提收费。
 * - 有了结果以后先收起「整理一下」：同一版再点只会给原结果；改了描述或照片（draft_revision 变了），上一版的结果和问题不再算数，整块回到只有「整理一下」。
 * 说法见 ./teachModel，不写「生成」、AI、模型。
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { newIdempotencyKey } from "@/shared/api/idempotency";
import { toApiError } from "@/shared/api/errors";
import { useOptionalCurrentHousehold } from "@/shared/session/householdContext";
import { Icon } from "@/shared/ui";
import {
  ANSWER_MAX, ANSWER_SEND, ANSWER_WHY_EMPTY, SIMPLER_USE, SPLIT_PICK, SPLIT_SAVE, SPLIT_SAVED, UNDERSTAND_ASK, UNDERSTAND_CHANGED, UNDERSTAND_DAILY, UNDERSTAND_GO,
  UNDERSTAND_IN_FLIGHT, UNDERSTAND_LEAD, UNDERSTAND_NO_QUESTION, UNDERSTAND_NOT_SENT, UNDERSTAND_NOT_SUPPORTED, UNDERSTAND_RETRY, UNDERSTAND_SIMPLER, UNDERSTAND_SPLIT,
  UNDERSTAND_TITLE, UNDERSTAND_UNAVAILABLE, UNDERSTAND_UNKNOWN, UNDERSTAND_UNUSABLE, UNDERSTAND_WHY_DIRTY, UNDERSTAND_WHY_EMPTY, sketchText, splitPicked,
} from "./teachModel";
import type { TeachOrder, TeachSketch, TeachUnderstandResult } from "./teachTypes";
import { teachKeys, useTeachApi } from "./useTeach";
import { TeachButton } from "./TeachButton";
import "./teach-understand.css";

/** 同一版正在整理时，过几秒刷新单子（不另发） */
export const IN_FLIGHT_REFRESH_MS = 5_000;

type Trouble = "in_flight" | "changed" | "not_sent" | "unavailable" | "daily" | "no_question" | "empty" | "other";
type Panel =
  | { kind: "result"; result: TeachUnderstandResult; revision: number }
  | { kind: "trouble"; trouble: Trouble; message: string; revision: number };

const TROUBLE_TEXT: Record<Exclude<Trouble, "other">, string> = {
  in_flight: UNDERSTAND_IN_FLIGHT, changed: UNDERSTAND_CHANGED, not_sent: UNDERSTAND_NOT_SENT, unavailable: UNDERSTAND_UNAVAILABLE,
  daily: UNDERSTAND_DAILY, no_question: UNDERSTAND_NO_QUESTION, empty: UNDERSTAND_WHY_EMPTY,
};

/** 后端的理由 → 页面上哪一种（HTTP-CONTRACT 第二节那张表） */
export function troubleOf(error: unknown): { trouble: Trouble; message: string; revision: number | null } {
  const e = toApiError(error);
  const details = (e.details ?? {}) as { reason?: string; draft_revision?: number };
  const reason = details.reason;
  const trouble: Trouble = reason === "understand_in_flight" ? "in_flight"
    : reason === "draft_changed" ? "changed"
    : reason === "understand_not_sent" ? "not_sent"
    : reason === "understand_unavailable" ? "unavailable"
    : reason === "understand_daily_limit" || e.status === 429 ? "daily"
    : reason === "no_pending_question" ? "no_question"
    : reason === "draft_text_required" ? "empty"
    : "other";
  return { trouble, message: trouble === "other" ? e.playerMessage : TROUBLE_TEXT[trouble], revision: typeof details.draft_revision === "number" ? details.draft_revision : null };
}

export function TeachUnderstand({ order, dirty }: { order: TeachOrder; dirty: boolean }) {
  const api = useTeachApi()!;
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const userId = useOptionalCurrentHousehold()?.userId ?? "-";
  const revision = order.draft_revision;
  const [panel, setPanel] = useState<Panel | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  const orderKey = teachKeys.order(userId, order.order_id);
  const put = (next: TeachOrder) => {
    queryClient.setQueryData(teachKeys.order(userId, next.order_id), next);
    void queryClient.invalidateQueries({ queryKey: teachKeys.orders(userId, order.pet_id) });
  };
  const handle = (result: TeachUnderstandResult) => {
    put(result.order);
    if (result.state === "ready" && result.order.state === "awaiting_confirm") {
      navigate(`/me/moves/${encodeURIComponent(result.order.order_id)}`, { replace: true, state: { teachFocus: "spec" } });
      return;
    }
    setPanel({ kind: "result", result, revision: result.order.draft_revision });
  };
  const fail = (error: unknown) => {
    const { trouble, message, revision: next } = troubleOf(error);
    setPanel({ kind: "trouble", trouble, message, revision: trouble === "changed" && next !== null ? next : revision });
    if (trouble === "changed") void queryClient.invalidateQueries({ queryKey: orderKey });
    if (trouble === "in_flight") {
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        void queryClient.invalidateQueries({ queryKey: orderKey }).then(() => setPanel((p) => (p?.kind === "trouble" && p.trouble === "in_flight" ? null : p)));
      }, IN_FLIGHT_REFRESH_MS);
    }
  };
  const understand = useMutation({ mutationFn: () => api.understand(order.order_id, revision), onSuccess: handle, onError: fail });
  const answer = useMutation({ mutationFn: (answers: string[]) => api.answers(order.order_id, revision, answers), onSuccess: handle, onError: fail });
  const rewrite = useMutation({
    mutationFn: (sketch: TeachSketch) => api.editDraft(order.order_id, { draft_text: sketchText(sketch), refs: order.refs ?? [] }),
    onSuccess: (next, sketch) => { put(next); setNote(splitPicked(sketch.name)); },
  });

  // 草稿换了一版：上一版的结果、问题不再算数
  const current = panel && panel.revision === revision ? panel : null;
  const result = current?.kind === "result" ? current.result : null;
  const trouble = current?.kind === "trouble" ? current.trouble : null;
  // 这一版已经有了结果：同一版再点只会给原结果（adm1），先收起「整理一下」，改了描述或照片（换了一版）再出来；
  // 说不清、整理没装、次数到了、正在整理也都不给按钮
  const stopped = Boolean(result) || trouble === "unavailable" || trouble === "daily" || trouble === "in_flight";
  const empty = !(order.draft_text ?? "").trim();
  const busy = understand.isPending || answer.isPending || rewrite.isPending;
  const why = busy ? null : empty ? UNDERSTAND_WHY_EMPTY : dirty ? UNDERSTAND_WHY_DIRTY : null;
  const go = () => { setNote(null); understand.mutate(); };

  return (
    <section className="ps-teach-card ps-teach-understand" aria-labelledby="teach-understand-title" data-teach-understand>
      <h3 id="teach-understand-title" className="ps-teach-group__title">
        <Icon name="sparkle" size={18} className="ps-teach-group__icon" />
        <span>{UNDERSTAND_TITLE}</span>
      </h3>
      <p className="ps-teach-note">{UNDERSTAND_LEAD}</p>

      {result ? <ResultView result={result} order={order} busy={busy} onAnswer={(values) => answer.mutate(values)} onRewrite={(sketch) => rewrite.mutate(sketch)} /> : null}
      {current?.kind === "trouble" ? <p className="ps-teach-understand__say" role="status" data-trouble={current.trouble}>{current.message}</p> : null}
      {note ? <p className="ps-teach-understand__say" role="status" data-understand-note>{note}</p> : null}
      {rewrite.error ? <p className="ps-teach-form__error" role="alert">{toApiError(rewrite.error).playerMessage}</p> : null}

      {stopped ? null : (
        <>
          {why ? <p className="ps-teach-form__why" id="teach-understand-why">{why}</p> : null}
          <TeachButton variant="primary" block busy={understand.isPending} unavailable={busy || empty || dirty} describedBy={why ? "teach-understand-why" : undefined} onClick={go} data-understand-go>
            {trouble === "not_sent" ? UNDERSTAND_RETRY : UNDERSTAND_GO}
          </TeachButton>
        </>
      )}
    </section>
  );
}

function ResultView({ result, order, busy, onAnswer, onRewrite }: {
  result: TeachUnderstandResult; order: TeachOrder; busy: boolean; onAnswer: (answers: string[]) => void; onRewrite: (sketch: TeachSketch) => void;
}) {
  const detail = result.result ?? null;
  switch (result.state) {
    case "need_answer":
      return <Questions key={(detail?.questions ?? []).join("|")} questions={(detail?.questions ?? []).slice(0, 2)} busy={busy} onAnswer={onAnswer} />;
    case "split":
      return <SplitGoals goals={detail?.split_goals ?? []} order={order} busy={busy} onPick={onRewrite} />;
    case "not_supported":
      return (
        <div className="ps-teach-understand__result" data-result="not_supported">
          <p className="ps-teach-understand__say" role="status">{UNDERSTAND_NOT_SUPPORTED}</p>
          {detail?.dropped?.length ? <ul className="ps-teach-understand__dropped">{detail.dropped.map((item, i) => <li key={i}>{item}</li>)}</ul> : null}
          {detail?.simplification ? (
            <div className="ps-teach-understand__sketch">
              <p className="ps-teach-note">{UNDERSTAND_SIMPLER}</p>
              <Sketch sketch={detail.simplification} />
              <TeachButton variant="secondary" block unavailable={busy} onClick={() => onRewrite(detail.simplification!)}>{SIMPLER_USE}</TeachButton>
            </div>
          ) : null}
        </div>
      );
    case "unusable":
      return <p className="ps-teach-understand__say" role="status" data-result="unusable">{UNDERSTAND_UNUSABLE}</p>;
    case "unknown":
      return <p className="ps-teach-understand__say" role="status" data-result="unknown">{UNDERSTAND_UNKNOWN}</p>;
    default:
      return null;
  }
}

function Questions({ questions, busy, onAnswer }: { questions: string[]; busy: boolean; onAnswer: (answers: string[]) => void }) {
  const [values, setValues] = useState(() => questions.map(() => ""));
  const first = useRef<HTMLInputElement>(null);
  useEffect(() => { first.current?.focus(); }, []);
  const filled = values.every((v) => v.trim());
  return (
    <form
      className="ps-teach-understand__result ps-teach-understand__questions"
      data-result="need_answer"
      onSubmit={(event) => {
        event.preventDefault();
        if (filled && !busy) onAnswer(values.map((v) => v.trim()));
      }}
    >
      <p className="ps-teach-understand__say" role="status">{UNDERSTAND_ASK}</p>
      {questions.map((question, i) => (
        <div key={i} className="ps-teach-understand__question">
          <label className="ps-teach-form__label" htmlFor={`teach-answer-${i}`}>{question}</label>
          <input
            ref={i === 0 ? first : undefined}
            id={`teach-answer-${i}`}
            className="ps-input"
            value={values[i]}
            maxLength={ANSWER_MAX}
            onChange={(event) => setValues((all) => all.map((v, j) => (j === i ? event.target.value : v)))}
          />
        </div>
      ))}
      {!filled && !busy ? <p className="ps-teach-form__why" id="teach-answer-why">{ANSWER_WHY_EMPTY}</p> : null}
      <TeachButton type="submit" variant="primary" block busy={busy} unavailable={!filled} describedBy={!filled && !busy ? "teach-answer-why" : undefined}>{ANSWER_SEND}</TeachButton>
    </form>
  );
}

function SplitGoals({ goals, order, busy, onPick }: { goals: TeachSketch[]; order: TeachOrder; busy: boolean; onPick: (sketch: TeachSketch) => void }) {
  const api = useTeachApi()!;
  const queryClient = useQueryClient();
  const userId = useOptionalCurrentHousehold()?.userId ?? "-";
  const keys = useRef(new Map<number, string>());
  const [saved, setSaved] = useState<ReadonlySet<number>>(new Set());
  const save = useMutation({
    // 另存的新草稿：refs 只能是空的（adm1：新建时只能是空的，参考走上传口）；每一个各用自己的幂等键，重发沿用
    mutationFn: (index: number) => {
      if (!keys.current.has(index)) keys.current.set(index, newIdempotencyKey("teach-split"));
      return api.createDraft(order.pet_id, { draft_text: sketchText(goals[index]), refs: [] }, keys.current.get(index)!);
    },
    onSuccess: (_created, index) => {
      setSaved((all) => new Set([...all, index]));
      void queryClient.invalidateQueries({ queryKey: teachKeys.orders(userId, order.pet_id) });
    },
  });
  return (
    <div className="ps-teach-understand__result" data-result="split">
      <p className="ps-teach-understand__say" role="status">{UNDERSTAND_SPLIT}</p>
      <ul className="ps-teach-understand__goals">
        {goals.map((goal, index) => (
          <li key={index} className="ps-teach-understand__goal">
            <Sketch sketch={goal} />
            <div className="ps-teach-understand__goal-actions">
              <TeachButton variant="secondary" unavailable={busy || save.isPending} onClick={() => onPick(goal)}>{SPLIT_PICK}</TeachButton>
              {saved.has(index) ? <span className="ps-teach-understand__saved">{SPLIT_SAVED}</span> : (
                <TeachButton variant="ghost" busy={save.isPending && save.variables === index} unavailable={busy || save.isPending} onClick={() => save.mutate(index)}>{SPLIT_SAVE}</TeachButton>
              )}
            </div>
          </li>
        ))}
      </ul>
      {save.error ? <p className="ps-teach-form__error" role="alert">{toApiError(save.error).playerMessage}</p> : null}
    </div>
  );
}

function Sketch({ sketch }: { sketch: TeachSketch }) {
  return (
    <div className="ps-teach-understand__sketch-body">
      <p className="ps-teach-understand__sketch-name">{sketch.name}</p>
      {sketch.steps.length ? <ol className="ps-teach-steps">{sketch.steps.map((step, i) => <li key={i}>{step}</li>)}</ol> : null}
    </div>
  );
}
